import { api } from 'redux/api/index.api';
import { getBusinessOrganizationVersion } from 'utils/constants';
import getUserFullName from 'utils/users';

export const organizationMembersApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchOrganizationMembers: builder.query({
      query: ({ organizationId, searchValue, limit = 100, filters, offset = 0 }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/members?limit=${limit}&offset=${offset}`,
        method: 'POST',
        body: {
          search_term: searchValue,
          roles: [...(filters?.roles || [])],
          with_processing_members: true,
        },
        headers: {
          'Cache-Control': 'No-Store',
        },
      }),
      providesTags: ['OrganizationMembers'],
      keepUnusedDataFor: 0,
      transformResponse: response => {
        const transformedMembers = response?.organization_members?.map(row => ({
          ...row,
          member: getUserFullName(row),
        }));
        return { ...response, organization_members: transformedMembers };
      },
    }),
    fetchOrganizationRoles: builder.query({
      query: ({ organizationId }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/organization-roles`,
        method: 'GET',
      }),
      providesTags: ['OrganizationRoles'],
      keepUnusedDataFor: 0,
    }),
    fetchOrganizationMembersCount: builder.query({
      query: ({ organizationId }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/members-count`,
        method: 'GET',
      }),
      transformResponse: response => response?.member_number,
      keepUnusedDataFor: 0,
    }),
    fetchOrganizationProjectRoles: builder.query({
      query: () => ({
        url: `v1.0/project-roles`,
        method: 'GET',
      }),
      providesTags: ['OrganizationRoles'],
      keepUnusedDataFor: 0,
    }),
    addUsersToOrganization: builder.mutation({
      query: ({ organizationId, roleUsers }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/add-members`,
        method: 'POST',
        headers: {
          'Cache-Control': 'No-Store',
          'Content-Type': 'application/json',
        },
        body: {
          role_users: roleUsers,
        },
      }),
      invalidatesTags: ['OrganizationMembers', 'OrganizationRoles'],
    }),
    updateOrganizationRoles: builder.mutation({
      query: ({ organizationId, roleId, userIds }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/edit-members-roles`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_role_id: roleId,
          user_ids: userIds,
        }),
      }),
      invalidatesTags: ['OrganizationMembers', 'OrganizationRoles'],
    }),
    removeMembersFromOrganization: builder.mutation({
      query: ({ organizationId, userIds }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${organizationId}/remove-members`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'No-Store' },
        body: JSON.stringify({
          user_ids: userIds,
        }),
      }),
      invalidatesTags: ['OrganizationMembers', 'OrganizationRoles'],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchOrganizationMembersQuery,
  useLazyFetchOrganizationMembersQuery,
  useFetchOrganizationRolesQuery,
  useFetchOrganizationMembersCountQuery,
  useFetchOrganizationProjectRolesQuery,
  useLazyFetchOrganizationRolesQuery,
  useAddUsersToOrganizationMutation,
  useUpdateOrganizationRolesMutation,
  useRemoveMembersFromOrganizationMutation,
} = organizationMembersApi;
